import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { API_URL } from "../config/api";
import { Ship, Loader2, Search, MapPin, AlertCircle } from "lucide-react";

const REF_TYPES = [
  { value: "equipmentReference", label: "Container" },
  { value: "transportDocumentReference", label: "Bill of Lading" },
  { value: "carrierBookingReference", label: "Booking" },
];

function eventLabel(e) {
  return e.transportEventTypeCode || e.equipmentEventTypeCode || e.shipmentEventTypeCode || e.eventType || "Event";
}
function classifier(e) {
  return { ACT: "Actual", EST: "Estimated", PLN: "Planned" }[e.eventClassifierCode] || e.eventClassifierCode || "";
}
function locName(e) {
  const l = e.eventLocation || e.location || {};
  return l.locationName || l.UNLocationCode || l.address?.city || "";
}

export default function HapagTrackPanel() {
  const token = localStorage.getItem("token");
  const [status, setStatus] = useState(null);
  const [refType, setRefType] = useState("equipmentReference");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/hapag/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false }));
    // eslint-disable-next-line
  }, []);

  const track = async () => {
    if (!reference.trim()) { toast.error("Enter a reference"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.get(`${API_URL}/hapag/track`, {
        params: { reference: reference.trim(), ref_type: refType },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(data);
      if (!data.found) toast.info("No Hapag events found for this reference");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hapag tracking failed");
    } finally {
      setLoading(false);
    }
  };

  const events = (result?.events || []).slice().sort((a, b) =>
    (b.eventDateTime || "").localeCompare(a.eventDateTime || ""));

  return (
    <div className="space-y-4" data-testid="hapag-track-panel">
      <div className={`rounded-xl border p-4 ${status?.configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
        <div className="flex items-center gap-2">
          <Ship className={`h-5 w-5 ${status?.configured ? "text-emerald-600" : "text-amber-600"}`} />
          <div>
            <div className="font-semibold text-slate-800">Hapag-Lloyd Track &amp; Trace</div>
            <div className="text-xs text-slate-500">DCSA v2 events — direct from Hapag (saves SeaRates points)</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border p-4 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {REF_TYPES.map((r) => (
            <Button key={r.value} size="sm" variant={refType === r.value ? "default" : "outline"}
              onClick={() => setRefType(r.value)} data-testid={`hapag-track-type-${r.value}`}>{r.label}</Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="hapag-track-input" placeholder="e.g., HLCU1234567" value={reference} onChange={(e) => setReference(e.target.value)} onKeyDown={(e) => e.key === "Enter" && track()} />
          <Button onClick={track} disabled={loading || !status?.configured} data-testid="hapag-track-btn" className="bg-[#eb6209] hover:bg-[#d4560a]">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Track
          </Button>
        </div>
      </div>

      {result && (
        <div data-testid="hapag-track-results">
          {events.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" /> No events found for {result.reference}.
            </div>
          ) : (
            <div className="rounded-xl border divide-y">
              {events.map((e, i) => (
                <div key={i} className="p-3 flex items-start gap-3" data-testid={`hapag-event-${i}`}>
                  <div className="mt-1"><MapPin className="h-4 w-4 text-[#eb6209]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800">{eventLabel(e)}</span>
                      {classifier(e) && <Badge variant="outline" className="text-[10px]">{classifier(e)}</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {e.eventDateTime ? new Date(e.eventDateTime).toLocaleString() : ""}
                      {locName(e) ? ` · ${locName(e)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
